import { useEffect, useRef, useState } from "react"

import { lookupUrl } from "@/lib/content-lookup"
import type { LookupResult } from "@/types/contents"

export interface UrlLookupState {
  result: LookupResult | null
  loading: boolean
  failed: boolean
}

interface InternalState {
  result: LookupResult | null
  fetching: boolean
  failed: boolean
  resolvedUrl: string
}

/**
 * Debounced, stale-safe wrapper over POST /content/lookup.
 * - Fires only when the URL *changes* (not on every render); a pre-filled
 *   initial URL does NOT auto-fetch (the edit form), because both the seen
 *   marker and `resolvedUrl` are seeded with it.
 * - `loading` is true during the debounce window *and* the request, derived
 *   from "current URL differs from the last resolved one" — so consumers
 *   never flash a stale result while the user is still typing.
 * - Each request is tied to its URL via an AbortController, so an
 *   out-of-order response is dropped rather than applied.
 */
export function useUrlLookup(url: string, debounceMs = 600): UrlLookupState {
  const trimmed = url.trim()

  const [state, setState] = useState<InternalState>({
    result: null,
    fetching: false,
    failed: false,
    resolvedUrl: trimmed,
  })

  // The last URL we dispatched for — seeded with the initial value so a
  // pre-filled URL doesn't auto-fetch on mount.
  const lastUrlRef = useRef(trimmed)

  useEffect(() => {
    const next = url.trim()
    if (!next || next === lastUrlRef.current) return

    const controller = new AbortController()
    const timer = setTimeout(async () => {
      lastUrlRef.current = next
      const result = await lookupUrl(next, controller.signal)
      if (controller.signal.aborted) return
      setState({
        result: result ?? null,
        fetching: false,
        failed: !result,
        resolvedUrl: next,
      })
    }, debounceMs)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [url, debounceMs])

  // A blank URL is a clean slate — never carry a prior failure/result.
  if (trimmed === "") {
    return { result: null, loading: false, failed: false }
  }

  // Pending = the visible URL hasn't been resolved yet (mid-debounce or
  // in-flight). Surfaced as `loading` so the prior result is never shown for
  // a URL the user has already changed.
  const pending = trimmed !== state.resolvedUrl

  return {
    result: pending ? null : state.result,
    loading: pending || state.fetching,
    failed: !pending && state.failed,
  }
}
